import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  subscribeToPlatformStats,
  subscribeToTotals,
  subscribeToCohorts,
  subscribeToPageviews,
  subscribeToTodayActiveUsers,
  subscribeToEmailStats,
  subscribeToEmailTotals,
  subscribeToPushStats,
  subscribeToPushTotals,
  subscribeToAccountDeletions,
  daysAgoKey,
} from '@coparent/shared/src/services/adminMetricsService';
import StatCard from '../components/admin/StatCard';
import DeletedAccountsCard from '../components/admin/DeletedAccountsCard';
import AdminControls from '../components/admin/AdminControls';
import DauMauChart from '../components/admin/DauMauChart';
import NewAccountsChart from '../components/admin/NewAccountsChart';
import FoundViaChart from '../components/admin/FoundViaChart';
import EmailsSentPanel from '../components/admin/EmailsSentPanel';
import PushSentPanel from '../components/admin/PushSentPanel';
import ExpenseActivityChart from '../components/admin/ExpenseActivityChart';
import LandingBlogChart from '../components/admin/LandingBlogChart';
import EngagementBreakdown from '../components/admin/EngagementBreakdown';
import ActivationFunnel from '../components/admin/ActivationFunnel';
import RetentionHeatmap from '../components/admin/RetentionHeatmap';
import PlatformSplitCard from '../components/admin/PlatformSplitCard';
import IosDownloadsChart from '../components/admin/IosDownloadsChart';
import AndroidDownloadsChart from '../components/admin/AndroidDownloadsChart';

const LOOKBACK_DAYS = 30;
// DauMauChart has its own 30/60/90 timeframe filter, so platformStats is
// fetched at the widest option and every other card is narrowed back to
// LOOKBACK_DAYS client-side — one query instead of a second subscription.
const MAX_CHART_DAYS = 90;

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

const AdminMetricsPage = () => {
  const [allStats, setAllStats] = useState([]);
  const [totals, setTotals] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [pageviews, setPageviews] = useState([]);
  const [liveDau, setLiveDau] = useState({ web: 0, ios: 0, android: 0, total: 0 });
  const [emailStats, setEmailStats] = useState([]);
  const [emailTotals, setEmailTotals] = useState(null);
  const [pushStats, setPushStats] = useState([]);
  const [pushTotals, setPushTotals] = useState(null);
  const [deletions, setDeletions] = useState([]);

  useEffect(() => {
    const unsubs = [
      subscribeToPlatformStats(MAX_CHART_DAYS, setAllStats),
      subscribeToTotals(setTotals),
      subscribeToCohorts(12, setCohorts),
      subscribeToPageviews(LOOKBACK_DAYS, setPageviews),
      subscribeToTodayActiveUsers(setLiveDau),
      subscribeToEmailStats(LOOKBACK_DAYS, setEmailStats),
      subscribeToEmailTotals(setEmailTotals),
      subscribeToPushStats(LOOKBACK_DAYS, setPushStats),
      subscribeToPushTotals(setPushTotals),
      subscribeToAccountDeletions(LOOKBACK_DAYS, setDeletions),
    ];
    return () => unsubs.forEach((u) => u && u());
  }, []);

  const today = todayKey();

  // Every card except DauMauChart is scoped to LOOKBACK_DAYS, so narrow the
  // wider subscription back down here rather than at each call site.
  const stats = useMemo(() => {
    const cutoff = daysAgoKey(LOOKBACK_DAYS);
    return allStats.filter((s) => (s.date || s.id || '') >= cutoff);
  }, [allStats]);

  // The daily rollup runs at 00:05 UTC for *yesterday*, so today's snapshot
  // never exists. `latestStat` is the newest available rolled-up day — use it
  // for tiles whose label says "today" but where slight lag is acceptable
  // (MAU, new accounts, platform split). Live-recomputed DAU is the exception.
  const latestStat = stats.length > 0 ? stats[stats.length - 1] : null;
  const yesterdayStat = stats.length >= 2 ? stats[stats.length - 2] : null;

  // The DAU chart specifically gets a synthetic in-progress data point for
  // today, so it stays consistent with the live tile. MAU is a trailing
  // 30-day count that barely moves day-to-day, so we carry yesterday's value
  // forward rather than recompute live (it would require unioning 30 active-
  // user subcollections client-side).
  const dauChartStats = useMemo(() => {
    if (!latestStat) return allStats;
    if (latestStat.id === today) return allStats;
    return [
      ...allStats,
      {
        id: today,
        date: today,
        dau: { ...liveDau },
        mau: latestStat.mau,
        inProgress: true,
      },
    ];
  }, [allStats, liveDau, latestStat, today]);

  const funnel = useMemo(() => {
    // Aggregate signup funnel across the full lookback — the rollup stamps
    // funnel totals in the _totals doc, but we also derive a range-scoped view.
    return {
      signups: stats.reduce((sum, s) => sum + (s.newAccounts || 0), 0),
      joinedFamily: stats.reduce((sum, s) => sum + (s.funnelJoinedFamily || 0), 0),
      firstExpense: stats.reduce((sum, s) => sum + (s.funnelFirstExpense || 0), 0),
      week1Active: stats.reduce((sum, s) => sum + (s.funnelWeek1Active || 0), 0),
    };
  }, [stats]);

  // Day-over-day delta against the prior rolled-up day. With today not rolled
  // up, we compare latest-rolled-up vs the day before it.
  const delta = (field) => {
    if (!latestStat || !yesterdayStat) return null;
    const a = latestStat[field] || 0;
    const b = yesterdayStat[field] || 0;
    return a - b;
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="page-title">Admin metrics</h1>
            <p className="page-subtitle">
              Live product health. Aggregates refresh via real-time Firestore subscriptions.
            </p>
          </div>
          <Link
            to="/admin/social-metrics"
            className="text-sm text-canopy hover:text-accent-700 font-semibold whitespace-nowrap"
          >
            Enter social metrics →
          </Link>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard
          label="DAU (all)"
          value={liveDau.total}
          accent="canopy"
        />
        <StatCard
          label="MAU"
          value={latestStat?.mau?.total || 0}
          accent="meadow"
        />
        <StatCard
          label="New accounts"
          value={latestStat?.newAccounts || 0}
          delta={delta('newAccounts')}
          accent="sunbeam"
        />
        <StatCard
          label="Expenses today"
          value={latestStat?.expensesCreated || 0}
          accent="meadow"
        />
        <StatCard
          label="$ logged today"
          value={(latestStat?.expensesCreatedCents || 0) / 100}
          format="currency"
          accent="canopy"
        />
        <DeletedAccountsCard deletions={deletions} />
      </div>

      {/* Lifetime tile strip */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Lifetime users" value={totals.usersCount || 0} accent="canopy" />
          <StatCard label="Lifetime families" value={totals.familiesCount || 0} accent="meadow" />
          <StatCard label="Lifetime expenses" value={totals.expensesCount || 0} accent="sunbeam" />
          <StatCard
            label="$ logged all-time"
            value={(totals.expensesTotalCents || 0) / 100}
            format="currency"
            accent="blush"
          />
        </div>
      )}

      {/* Primary charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <DauMauChart stats={dauChartStats} />
        <NewAccountsChart stats={stats} />
      </div>

      {/* Self-reported acquisition (the signup question) alongside expense
          activity. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <FoundViaChart stats={stats} />
        <ExpenseActivityChart stats={stats} />
      </div>

      {/* Outbound comms: emails (Resend) + push notifications (Expo), each
          half-width and stacked by type. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <EmailsSentPanel emailStats={emailStats} emailTotals={emailTotals} />
        <PushSentPanel pushStats={pushStats} pushTotals={pushTotals} />
      </div>

      {/* App store downloads: iOS (App Store Connect) + Android (Google Play). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <IosDownloadsChart stats={stats} />
        <AndroidDownloadsChart stats={stats} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <LandingBlogChart pageviews={pageviews} />
        <EngagementBreakdown stats={stats} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <ActivationFunnel funnel={funnel} />
        <PlatformSplitCard todayStats={{ ...latestStat, ...totals }} />
      </div>

      <div className="mb-4">
        <RetentionHeatmap cohorts={cohorts} />
      </div>

      <div className="mb-4">
        <AdminControls />
      </div>
    </div>
  );
};

export default AdminMetricsPage;
